import PropTypes from 'prop-types'
import React from 'react'
import { push } from 'react-router-redux'
import { withRouter } from 'react-router'
import cx from 'classnames'

import moment, { displayFormat, urlFormat, pastCutoff } from 'helpers/moment'
import { makeOptions } from 'helpers/components'
import { compareParseInt, guid } from 'helpers/index'

import Breadcrumb from 'components/Breadcrumb'
import Button from 'components/Button'
import DateComponent from 'components/Date'
import Input from 'components/Form/Input'
import PricelistDropdown from 'components/Pricelist/Dropdown'
import Select from 'components/Form/Select'
import Textarea from 'components/Form/Textarea'

import classes from './OrderForm.scss'
import labelClasses from 'components/Form/Label/FormLabel.scss'

export class OrderForm extends React.Component {
  constructor (props) {
    super(props)

    this.state = { customer: null, specialOrdering: false, specialOrderProduct: false }

    this.submitInputId = `submit-${guid()}`
    this._current_product = null
  }

  componentWillReceiveProps (nextProps) {
    // Note: compareParseInt handles isNan === isNan (no product_ids sent yet)
    const productChanged = !compareParseInt(this.props.fields.product_id.value, nextProps.fields.product_id.value)

    if (nextProps.user && nextProps.user.can_special_order) {
      if (
        nextProps.pricelist && nextProps.pricelist.length && // pricelist exists
        (nextProps.pricelist !== this.props.pricelist || !this.state.specialOrderProduct) // pricelists are different (or not set yet)
      ) {
        this.setSpecialOrderState(nextProps)
      } else if (productChanged) {
        // update special order props when product_id has changed
        this.setSpecialOrderState(nextProps)
      }
    }

    // set specialOrdering to false if no product is selected (couldn't be special ordering)
    if (!nextProps.fields.product_id.value && this.state.specialOrdering) {
      this.setState({ specialOrdering: false })
    }

    // set customer to null if none is selected
    if (!nextProps.fields.customer_id.value && this.state.customer) {
      this.setState({ customer: null })
    }

    // if the product changed, let's validate uom/qty/options are still valid for the new product..
    if (productChanged) {
      this.validateDropdowns(nextProps)
    }
  }

  componentDidMount () {
    this.setSpecialOrderState() // always do at mount

    if (!this.props.fields.product_id.value && this.state.specialOrdering) {
      this.setState({ specialOrdering: false })
    }

    if (!this.props.fields.customer_id.value && this.state.customer) {
      this.setState({ customer: null })
    }
  }

  isMultipleCustomers = (props = this.props) => {
    return props.isAdmin ||
      props.user && props.user.customers && props.user.customers.length > 1 ||
      props.customers && props.customers.length > 1
  };

  onSubmit = (...args) => {
    let submitResponse = this.props.handleSubmit(...args)
    if (submitResponse) submitResponse.then(model => this.afterSubmit(model))
  };

  afterSubmit = model => {
    if (!this.props.redirectToPricelistAfterSubmit) return

    let date = this.props.fields.date.value || this.props.order.date || this.props.date
    return this.props.dispatch(push(
      `/pricelist${(date && `/${moment(date).format(urlFormat)}` || '')}`
    ))
  };

  handleDelete = () => {
    if (this.props.handleDelete) this.props.handleDelete(this.props.order.id)
  };

  getBasePricelist (props = this.props, useState = true) {
    // we're filtering out the special order product here for !user.can_special_order
    const collection = props.pricelist
    if (props.user && props.user.can_special_order === true) return collection

    if (useState && this.state.specialOrderProduct && this.state.specialOrderProduct.product_id) { // this is a much quicker filter
      return collection.filter(product => product.product_id !== this.state.specialOrderProduct.product_id)
    }

    return collection.filter(product => !this.isSpecialOrder(product))
  }

  getFilteredPricelist = () => {
    let collection = this.getBasePricelist()

    // customer may not be selected, or non-admins cannot see the customer, and their pricelist will have already been sorted
    if (!this.props.fields.customer_id || !this.props.fields.customer_id.value) return collection

    let filtered = collection.filter(product => this.customerCanOrderProduct(product))
    if (filtered.length === collection.length) return collection // send the same one so we don't force a refresh
    return filtered
  };

  getProduct = (productId = this.props.fields.product_id.value, props = this.props) => {
    if (this._current_product && this._current_product.id === productId) return this._current_product

    const product = this.getBasePricelist(props).find(o =>
      compareParseInt(o.product_id, productId)
    )

    this._current_product = product
    return product
  };

  customerCanOrderProduct = (product, customerId = this.props.fields.customer_id.value) => {
    if (!product) return false
    if (!product.customers || !Array.isArray(product.customers) || !product.customers.length) return true

    return product.customers.map(o => o.id).includes(customerId)
  };

  isSpecialOrder = product => {
    if (!product) return false
    if (!product.name || typeof product.name.toLowerCase !== 'function') return false
    if (this.state.specialOrderProduct && this.state.specialOrderProduct.product_id) {
      return product.product_id === this.state.specialOrderProduct.product_id
    }

    const productName = product.name.toLowerCase()

    return ['off list', 'offlist', 'special order', 'off-list'].some(name => productName.includes(name))
  };

  findSpecialOrderProduct = (props = this.props) => {
    const product = props.pricelist.find(product => this.isSpecialOrder(product)) // directly access the pricelist
    if (!product || !product.product_id) return false

    return product
  };

  toggleSpecialOrderProduct = () => {
    const product = this.state.specialOrderProduct
    if (!product || !product.product_id) return false

    if (this.props.fields.product_id.value === product.product_id) {
      this.props.fields.product_id.onChange(null)
      this.changedProduct()
      return
    }

    // call what we'd call if the user did it
    this.props.fields.product_id.onChange(product.product_id)
    this.changedProduct(product.product_id)
  };

  setSpecialOrderState = (props = this.props) => {
    const product = this.state.specialOrderProduct && this.state.specialOrderProduct.product_id
      ? this.state.specialOrderProduct
      : this.findSpecialOrderProduct(props)

    const status = product && compareParseInt(product.product_id, props.fields.product_id.value)

    let stateUpdates = {}
    if (product && product !== this.state.specialOrderProduct &&
      !(this.state.specialOrderProduct && product.product_id === this.state.specialOrderProduct.product_id)
    ) {
      stateUpdates.specialOrderProduct = product
    }

    if (this.state.specialOrderProduct !== status) stateUpdates.specialOrdering = status

    if (Object.keys(stateUpdates).length) this.setState(stateUpdates)
  };

  validateDropdowns = (props = this.props) => {
    this.resetInvalidSelection(props, 'option_id', this.getOptionOptions(props))

    // only if qty field is a dropdown
    if (this.getQtyOptions(props).length) this.resetInvalidSelection(props, 'qty', this.getQtyOptions(props))

    this.resetInvalidSelection(props, 'uom_id', this.getUomOptions(props))
  };

  resetInvalidSelection = (props = this.props, field, dropdownOptions = []) => {
    if (!props.fields[field] || !props.fields[field].value) return
    const found = dropdownOptions.find(o => o.value === props.fields[field].value)

    if (!found) {
      props.fields[field].onChange(null)
    }
  };

  onChangeProduct = (...args) => {
    this.props.fields.product_id.onChange(...args)
    let value = args[0] && args[0].value || args[0]

    if (!value) return // nothing to change

    this.changedProduct(value)
  };

  changedProduct = productId => {
    const product = productId && this.getProduct(productId)
    this.setSpecialOrderState()

    if (product && product.default_option_id && !this.props.fields.option_id.touched) {
      this.props.fields.option_id.onChange(product.default_option_id)
    }
    if (product && product.default_uom_id && !this.props.fields.uom_id.touched) {
      this.props.fields.uom_id.onChange(product.default_uom_id)
    }
  };

  onChangeCustomer = (...args) => {
    this.props.fields.customer_id.onChange(...args)
    let value = args[0] && args[0].value || args[0]

    if (!value) {
      this.setState({ customer: null })
      return // nothing to change
    }

    this.changedCustomer(value)
  };

  changedCustomer = customerId => {
    const customer = customerId && this.props.customers.find(o => compareParseInt(o.id, customerId))
    const product = this.getProduct()

    this.setState({ customer: customer })

    // if customer doesn't have access to this product
    if (!this.customerCanOrderProduct(product, customer.id)) {
      this.props.fields.product_id.onChange(null)
      this.onChangeProduct(null)
    }
  };

  getCustomerNotes = () => { // admin only
    if (!this.props.isAdmin || !this.state || !this.state.customer) return null
    let customer = this.state.customer

    // Automatically route the order.
    let route = (customer.routes || []).find(o =>
      compareParseInt(o.day_of_week, moment(this.props.fields.date.value).format('d'))
    ) || (customer.routes || []).find(o => o.day_of_week === '*')

    if (!route || !route.name) return null

    return `Route ${route.name.capitalize()} ${route.region && route.region !== route.name && `to ${route.region}` || ''}`.trim()
  };

  getProductNotes = () => { // admin only
    if (!this.props.isAdmin || !this.state || !this.state.customer) return null

    return this.state.customer.notes || null
  };

  getOptionOptions = (props = this.props) => {
    if (props.isAdmin) return makeOptions(props.options) // admins get all specs

    const product = this.getProduct(props.fields.product_id.value, props)

    // if no product.type can be found, we'll just send all options
    if (!product || !product.type) return makeOptions(props.options)

    const constrain = (product && product.type && product.type.constrain_options_to_pricelist)
    let options = product.type.options && product.type.options.length
      ? product.type.options
      : props.options

    if (constrain) {
      // constrain the options by prices.option_id and default no options to the original array (from type)
      const productPriceOptions = (product.prices || []).filter(o => o && o.price && o.option_id).map(o => o.option_id)
      const constrainedOptions = productPriceOptions.length && options.filter(option => productPriceOptions.includes(option.id)) || []
      options = constrainedOptions.length && constrainedOptions || options
    }

    return makeOptions(options)
  };

  getQtyOptions = (props = this.props) => {
    if (props.isAdmin) return [] // admins get to input any qty

    const product = this.getProduct(props.fields.product_id.value, props)
    if (!product || !product.increment_by) return []

    let iterations = parseInt(product.increment_iterations, 10)
    if (!iterations || iterations < 1 || iterations > 250) iterations = 20 // we don't want any bad loops.....

    let options = []
    for (let i = 1; i <= iterations; i++) {
      options.push(product.increment_by * i)
    }

    return options.map(v => ({ value: v, label: v.toString() }))
  };

  getUomOptions = (props = this.props) => {
    if (props.isAdmin) return makeOptions(props.uoms) // admins get all uoms

    const product = this.getProduct(props.fields.product_id.value, props)
    const uoms = product && product.type && product.type.uoms && product.type.uoms.length
      ? product.type.uoms
      : props.uoms

    return makeOptions(uoms)
  };

  shouldShowOptions = () => {
    const product = this.getProduct()
    if (!product || !product.name) return true
    const optionOptions = this.getOptionOptions() || []
    return product && product.type && Array.isArray(product.type.options) && !!product.type.options.length && !!optionOptions.length
  };

  renderCustomerField = () => {
    if (!this.isMultipleCustomers()) return <Input type='hidden' {...this.props.fields.customer_id} />

    return <Select label='Customer'
      placeholder='select a customer'
      note={this.getCustomerNotes()}
      selectOptions={makeOptions(this.props.customers)}
      {...this.props.fields.customer_id}

      onChange={this.onChangeCustomer}
      noMobileSearch={
        !this.props.isAdmin // users won't be able to search this field
      }
    />
  };

  renderQtyField = () => {
    const product = this.getProduct() || {}
    const qtyOptions = this.getQtyOptions() || []

    if (!product.increment_by || !qtyOptions.length) {
      return <Input label='Quantity' placeholder='quantity' pattern='[0-9]+(\.[0-9]{0,})?' {...this.props.fields.qty} />
    }

    return <Select label='Quantity'
      placeholder='quantity'
      selectOptions={qtyOptions}
      inputProps={{ type: 'number' }}
      noMobileSearch={
        !this.props.isAdmin // users won't be able to search this field
      }
      {...this.props.fields.qty}
    />
  };

  renderQtyUomFields = () => {
    return <div className={classes['qty-uom']}>
      {this.renderQtyField()}

      <Select label='Unit of Measure'
        placeholder='select a unit'
        selectOptions={this.getUomOptions()}
        options={['expandable--tiny', 'expandable--small']}
        filterModelFields={['shorthand']}
        {...this.props.fields.uom_id}
      />
    </div>
  };

  getErrorMessage = () => {
    if (!this.props.isAdmin) { // non-admin only errors
      const pastCutoffMessage = pastCutoff(this.props.date)
      if (pastCutoffMessage) return pastCutoffMessage

      if (this.props.editing && this.props.order.printed_at) {
        return `Cannot edit an order once it's started fulfillment.`
      }
    }

    return null
  };

  getWarningMessage = () => {
    if (this.props.isAdmin) { // admin-only warnings
      if (this.props.editing && this.props.order.printed_at) {
        return `This order has already been printed.`
      }

      const pastCutoffMessage = pastCutoff(this.props.date)
      if (pastCutoffMessage) return `By the way, it's past the cutoff to add orders.`
    }

    return null
  };

  render () {
    const currentIsSpecialOrder = this.state.specialOrderProduct && this.props.fields.product_id.value &&
      compareParseInt(this.props.fields.product_id.value, this.state.specialOrderProduct.product_id)

    const className = cx({
      [classes['form']]: classes['form'],
      [classes['form--two-fifths']]: this.props.partial && classes['form--two-fifths'],
      [classes['form--partial']]: this.props.partial && classes['form--partial'],
      [classes['form--disabled']]: !!this.getErrorMessage() && classes['form--disabled']
    })

    // this onSubmit is how redux-form wants it!
    return <form onSubmit={this.onSubmit} className={className}>
      <input type='submit' id={this.submitInputId} className={classes.hidden} />

      <header className={classes['header']}>
        {!this.props.editing && !this.props.partial &&
          <Breadcrumb to={`/pricelist${(this.props.date && `/${moment(this.props.date).format(urlFormat)}` || '')}`}>
            Back to Pricelist
          </Breadcrumb>
        }

        <div className={classes['header-title']}>
          {this.props.editing && 'Editing' || 'Add'} an Item

          {this.props.editing && <span className={classes['header-title--sub']}>
            on {moment(this.props.fields.date.value).format(displayFormat)}
          </span> || <span className={cx({
            [classes['header-title--sub']]: true,
            [classes['header-title--dropdown-date']]: true
          })}>
            <DateComponent {...this.props}
              date={this.props.fields.date.value}
              textFormat='for delivery on {0}'
              baseUrl='orders'
            />
          </span>}
        </div>

        {this.props.editing && !this.getErrorMessage() &&
          <div className={classes['header-buttons']}>
            <Button confirm options={['red', 'small']} callback={this.handleDelete}>
              Delete Item
            </Button>
          </div>
        }

        {this.getErrorMessage() && <div className={classes['form-notice--error']}>
          {this.getErrorMessage()}
        </div>}

        {!this.getErrorMessage() && this.getWarningMessage() && <div className={classes['form-notice--warning']}>
          {this.getWarningMessage()}
        </div>}
      </header>

      <fieldset className={this.getErrorMessage() && classes['fieldset--disabled'] || ''}>
        {this.renderCustomerField()}

        {this.props.isAdmin && this.renderQtyUomFields()}

        {/* Custom Label Wrapper for Pricelist dropdown (pretty easily offset into PricelistDropdown). */}
        <div className={labelClasses['label']}>
          <div className={labelClasses['title']}>
            Product

            {this.props.user && this.props.user.can_special_order && this.state.specialOrderProduct &&
              <div className={labelClasses['title-buttons']}>
                <Button options={['tiny']}
                  onlyOnce={false}
                  callback={this.toggleSpecialOrderProduct}
                  tabIndex={-1}
                >
                  {currentIsSpecialOrder && 'Cancel Off List Request' || 'Request Off List Item'}
                </Button>
              </div>
            }
          </div>

          <div className={labelClasses['content']}>
            <PricelistDropdown
              label={null}
              pricelist={this.getFilteredPricelist()}
              options={this.props.options}
              uoms={this.props.uoms}
              date={moment(this.props.fields.date.value)}
              filterModelFields={[
                'name', 'scientific_name', 'details',
                'tasting_notes', 'fishing_vessel', 'sourcing_method', 'sourcing_location',
                'type.name', 'category.name'
              ]}
              disabled={!!currentIsSpecialOrder}

              {...this.props.fields.product_id}

              onChange={this.onChangeProduct}
              note={this.getProductNotes()}
            />
          </div>
        </div>

        {this.state.specialOrdering &&
          <Textarea label='Note' placeholder='Put your off list item request here.' maxLength={50} {...this.props.fields.notes} />
        }

        {!this.props.isAdmin && this.renderQtyUomFields()}

        {this.shouldShowOptions() && <Select label='Spec'
          placeholder='select a spec'
          selectOptions={this.getOptionOptions()}
          options={['expandable--tiny', 'expandable--small']}
          tabSelectsValue={false}
          {...this.props.fields.option_id}
          required={!this.props.isAdmin}
        />}

        {!this.state.specialOrdering &&
          <Textarea label='Note' placeholder='special requests go here' maxLength={50} {...this.props.fields.notes} />}
      </fieldset>

      <footer className={classes['footer']}>
        <Button element={'label'} htmlFor={this.submitInputId} disabled={!!this.getErrorMessage()} stick options={['green', 'block']}>
          {this.props.editing && 'Update Item' || 'Add to Cart'}
        </Button>
      </footer>
    </form>
  }
}

OrderForm.propTypes = {
  // state
  order: PropTypes.object,
  pricelist: PropTypes.array.isRequired,
  options: PropTypes.array.isRequired,
  uoms: PropTypes.array.isRequired,
  customers: PropTypes.array,

  // state.auth
  isAdmin: PropTypes.bool,
  user: PropTypes.object.isRequired,

  // redux-form
  dispatch: PropTypes.func.isRequired,
  onSubmit: PropTypes.func, // actual submit function
  handleSubmit: PropTypes.func.isRequired, // redux-form function
  handleDelete: PropTypes.func,
  fields: PropTypes.object.isRequired,
  submitting: PropTypes.bool.isRequired,

  // from parent
  editing: PropTypes.bool,
  partial: PropTypes.bool,
  redirectToPricelistAfterSubmit: PropTypes.bool,
  date: PropTypes.any,

  // routing
  location: PropTypes.object,
  params: PropTypes.object
}

OrderForm.defaultProps = {
  isAdmin: false,
  editing: false,
  partial: false,
  redirectToPricelistAfterSubmit: false
}

export default withRouter(OrderForm)



// WEBPACK FOOTER //
// ./src/components/Orders/Form/OrderForm.js